import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useDeepLink } from "../../hooks/useDeepLink";
import { CommentHighlight } from "../../components/Reviews/CommentHighlight";

interface Comment {
  id: string;
  content: string;
  authorName: string;
  createdAt: string;
}

interface Review {
  id: string;
  content: string;
  rating: number;
  authorName: string;
  createdAt: string;
  comments: Comment[];
}

const DeepLinkResolverPage: React.FC = () => {
  const { reviewId } = useParams<{ reviewId: string }>();
  const { commentId, highlightActive } = useDeepLink();

  const [review, setReview] = useState<Review | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!reviewId) {
      setNotFound(true);
      setLoading(false);
      return;
    }

    const apiUrl =
      process.env.REVIEWS_API_URL ||
      "https://api.tst.carreira.cloud/reviews/api/v1";
    const authToken =
      localStorage.getItem("authToken") || sessionStorage.getItem("authToken");

    fetch(`${apiUrl}/reviews/${reviewId}`, {
      headers: {
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      },
    })
      .then((res) => {
        if (!res.ok)
          throw Object.assign(new Error("Not found"), { status: res.status });
        return res.json() as Promise<Review>;
      })
      .then((data) => {
        setReview(data);
      })
      .catch(() => {
        setNotFound(true);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [reviewId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-500">Loading review...</p>
      </div>
    );
  }

  if (notFound || !review) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-500">Review not found.</p>
      </div>
    );
  }

  const targetComment = commentId
    ? review.comments.find((c) => c.id === commentId)
    : null;

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="text-xl font-semibold text-gray-900">Review</h1>
        <p className="text-sm text-gray-500 mt-1">by {review.authorName}</p>
        <div className="mt-3 text-gray-700">{review.content}</div>
        <div className="mt-1 text-sm text-yellow-600">
          Rating: {review.rating} / 5
        </div>
      </div>

      {review.comments.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-sm font-medium text-gray-700 uppercase tracking-wide">
            Comments
          </h2>
          {review.comments.map((comment) => {
            const isTarget = comment.id === commentId;
            return (
              <CommentHighlight
                key={comment.id}
                active={isTarget && highlightActive}
              >
                <div className="p-3 rounded border border-gray-200 bg-white">
                  <p className="text-sm font-medium text-gray-800">
                    {comment.authorName}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">
                    {comment.content}
                  </p>
                </div>
              </CommentHighlight>
            );
          })}
        </div>
      )}

      {commentId && !targetComment && (
        <p className="text-sm text-gray-400 mt-4">
          The linked comment could not be found.
        </p>
      )}
    </div>
  );
};

export default DeepLinkResolverPage;
